import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  BookmarkPlus,
  Check,
  ChevronDown,
  Loader2,
  Pencil,
  Send,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  askReportAnalyst,
  fetchReportOverview,
  type ReportChart,
  type ReportCoverage,
  type ReportGa4Block,
  type ReportOverview,
  type ReportPaidBlock,
  type ReportRow,
  type ReportTheme,
  type ReportUnavailable,
} from "../api";
import { WolfLogo } from "../components/WolfLogo";
import { useDashboard } from "../context/DashboardContext";
import { Markdown } from "../lib/markdown";
import { ReportChartLibrary } from "./ReportChartLibrary";
import {
  Chart,
  CoverageBadge,
  CoverageNote,
  EmptyPanel,
  fmtInt,
  fmtMoney,
  fmtPct,
  fmtRatio,
  num,
} from "./report-ui";
import { HiddenTray, Reorderable } from "./report-blocks";
import { useReportLayout } from "./report-layout";
import "./client-report.css";

const PERIODS = [7, 30, 90, 180];

/** Etiquetas legibles de cada bloque y card, para la barra de arrastre y la
 *  bandeja de ocultos. Sin esto, la bandeja mostraría ids como "gads:terms". */
const BLOCK_LABELS: Record<string, string> = {
  ga4: "Sitio web", gads: "Google Ads", meta: "Meta Ads",
  "ga4:overview": "Visión general y evolución", "ga4:funnel": "Embudo de compra",
  "ga4:weekdays": "Día que más convierte", "ga4:sources": "Fuentes de tráfico",
  "ga4:sesiones": "Sesiones", "ga4:usuarios": "Usuarios", "ga4:compras": "Compras",
  "ga4:unavailable": "Métricas no conectadas", "ga4:articulos": "Artículos comprados",
  "ga4:tasaConversion": "Tasa de conversión", "ga4:ticketPromedio": "Ticket promedio",
  "ga4:ingresos": "Total de ingresos", "ga4:interacciones": "% Interacciones", "ga4:rebote": "Tasa de rebote",
  "gads:overview": "Visión general y evolución", "gads:alert": "Alerta de medición",
  "gads:search": "Campañas Search", "gads:pmax": "Campañas PMax",
  "gads:other": "Campañas sin clasificar", "gads:keywords": "Palabras clave",
  "gads:terms": "Términos de búsqueda",
  "meta:overview": "Visión general y evolución", "meta:alert": "Alerta de medición",
  "meta:campaigns": "Detalle por campaña", "meta:ads": "Muestra de anuncios",
  ...Object.fromEntries(["gads", "meta"].flatMap((platform) => [
    [`${platform}:inversion`, "Inversión"], [`${platform}:impresiones`, "Impresiones"],
    [`${platform}:clics`, "Clics"], [`${platform}:ctr`, "CTR"], [`${platform}:cpc`, "CPC medio"],
    [`${platform}:cpm`, "CPM"], [`${platform}:conversiones`, "Conversiones"],
    [`${platform}:campanas`, "Campañas activas"], [`${platform}:rentabilidad`, "Rentabilidad"],
    [`${platform}:cuotaImpresiones`, "Cuota de impresiones"],
  ])),
};

function Tile({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className={muted ? "rep-tile is-muted" : "rep-tile"}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

/** Métrica que el Looker original mostraba y esta fuente no tiene. Se dibuja
 *  como ausencia explícita para que nadie la lea como un cero. */
function MissingTile({ item }: { item: ReportUnavailable }) {
  return (
    <div className="rep-tile is-missing" title={item.reason}>
      <span>{item.metric}</span>
      <strong>N/D</strong>
      <i>No conectado</i>
    </div>
  );
}

function Table({
  title,
  headers,
  rows,
  coverage,
  emptyCopy,
}: {
  title: string;
  headers: string[];
  rows: Array<Array<string | number>>;
  coverage?: ReportCoverage;
  emptyCopy?: string;
}) {
  return (
    <article className="rep-panel">
      <div className="rep-panel-head">
        <h3>{title}</h3>
        {coverage && <CoverageBadge coverage={coverage} />}
      </div>
      {coverage && <CoverageNote coverage={coverage} />}
      {rows.length ? (
        <div className="rep-table-scroll">
          <table>
            <thead>
              <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="rep-empty">{emptyCopy ?? "Esta fuente no tiene filas para el cliente y período seleccionados."}</p>
      )}
    </article>
  );
}

function toChart(
  title: string,
  rows: ReportRow[],
  labelKey: string,
  series: Array<{ key: string; label: string; format: ReportChart["series"][number]["format"] }>,
  coverage: ReportCoverage,
): ReportChart {
  return {
    title,
    chartType: "combo",
    dimension: { id: labelKey, label: labelKey },
    labels: rows.map((row) => String(row[labelKey] ?? "")),
    series: series.map((serie) => ({
      id: serie.key,
      label: serie.label,
      format: serie.format,
      values: rows.map((row) => num(row[serie.key])),
    })),
    days: 0,
    platform: null,
    coverage,
    source: "overview",
  };
}

/** Contexto de edición que reciben las secciones para poder reordenarse. */
interface LayoutControls {
  layout: ReturnType<typeof useReportLayout>;
}

/** Envuelve una sección con su riel y renderiza sus bloques en el orden guardado. */
function SectionShell({
  id,
  variant,
  title,
  coverage,
  layout,
  blocks,
}: {
  id: string;
  variant: string;
  title: string;
  coverage?: ReportCoverage;
  layout: LayoutControls["layout"];
  blocks: Record<string, ReactNode>;
}) {
  const order = layout.layout.blocks[id] ?? [];
  return (
    <Reorderable
      id={id}
      kind="section"
      label={title}
      editing={layout.editing}
      onMove={layout.moveSection}
      onHide={layout.hide}
    >
      <section className={`rep-section is-${variant}`}>
        <aside className="rep-section-rail">
          <span>Rendimiento</span><strong>{title}</strong>
          {coverage && <CoverageBadge coverage={coverage} />}
        </aside>
        <div className="rep-section-body">
          {order
            .filter((blockId) => blocks[blockId] && !layout.isHidden(blockId))
            .map((blockId) => (
              <Reorderable
                key={blockId}
                id={blockId}
                kind="block"
                label={BLOCK_LABELS[blockId] ?? blockId}
                editing={layout.editing}
                onMove={(dragged, target) => layout.moveBlock(id, dragged, target)}
                onHide={layout.hide}
              >
                {blocks[blockId]}
              </Reorderable>
            ))}
        </div>
      </section>
    </Reorderable>
  );
}

/** Grupo de cards reordenables y ocultables individualmente. */
function TileGroup({
  groupId,
  layout,
  tiles,
  aside,
}: {
  groupId: string;
  layout: LayoutControls["layout"];
  tiles: Record<string, ReactNode>;
  aside?: ReactNode;
}) {
  const order = layout.layout.cards[groupId] ?? Object.keys(tiles);
  return (
    <div className="rep-overview">
      <div className="rep-overview-tiles">
        <span className="rep-overview-label">Visión general</span>
        <div className="rep-tiles">
          {order
            .filter((cardId) => tiles[cardId] && !layout.isHidden(cardId))
            .map((cardId) => (
              <Reorderable
                key={cardId}
                id={cardId}
                kind="card"
                editing={layout.editing}
                onMove={(dragged, target) => layout.moveCard(groupId, dragged, target)}
                onHide={layout.hide}
              >
                {tiles[cardId]}
              </Reorderable>
            ))}
        </div>
      </div>
      {aside}
    </div>
  );
}

function Ga4Section({ block, layout }: { block: ReportGa4Block; layout: LayoutControls["layout"] }) {
  const totals = block.totals;
  if (block.coverage.state === "SIN_DATOS") {
    return (
      <section className="rep-section is-ga4">
        <aside className="rep-section-rail"><span>Rendimiento</span><strong>Sitio web</strong></aside>
        <div className="rep-section-body">
          <EmptyPanel title="Sitio web (GA4)"
            reason="Este cliente no tiene propiedad GA4 con datos en la ventana. No hay sesiones, compras ni ingresos que mostrar; conectar la propiedad en el bridge habilita toda esta sección." />
        </div>
      </section>
    );
  }

  const tiles: Record<string, ReactNode> = {
    "ga4:sesiones": <Tile label="Sesiones" value={fmtInt(totals.sesiones)} />,
    "ga4:usuarios": <Tile label="Usuarios" value={fmtInt(totals.usuarios)} />,
    "ga4:compras": <Tile label="Compras" value={fmtInt(totals.compras)} />,
    "ga4:unavailable": <>{block.unavailable.map((item) => <MissingTile key={item.metric} item={item} />)}</>,
    "ga4:interacciones": <Tile label="% Interacciones" value={fmtPct(totals.interacciones)} />,
    "ga4:rebote": <Tile label="Tasa de rebote" value={fmtPct(totals.rebote)} />,
    "ga4:articulos": <Tile label="Artículos comprados" value={fmtInt(totals.articulos)} />,
    "ga4:tasaConversion": <Tile label="Tasa de conversión" value={fmtPct(totals.tasaConversion)} />,
    "ga4:ticketPromedio": <Tile label="Ticket promedio" value={fmtMoney(totals.ticketPromedio)} />,
    "ga4:ingresos": <Tile label="Total de ingresos" value={fmtMoney(totals.ingresos)} />,
  };

  const blocks: Record<string, ReactNode> = {
    "ga4:overview": (
      <TileGroup
        groupId="ga4:overview"
        layout={layout}
        tiles={tiles}
        aside={<Chart chart={toChart("Tráfico e ingresos en el sitio", block.daily, "periodo", [
          { key: "sesiones", label: "Sesiones", format: "integer" },
          { key: "ingresos", label: "Ingresos", format: "money" },
        ], block.coverage)} />}
      />
    ),
    "ga4:funnel": (
      <div className="rep-tiles is-funnel">
        <Tile label="Vistas de producto" value={fmtInt(totals.vistasProducto)} muted />
        <Tile label="Añadidos al carrito" value={fmtInt(totals.agregadosCarrito)} muted />
        <Tile label="Inicios de pago" value={fmtInt(totals.iniciosPago)} muted />
        <Tile label="Compras" value={fmtInt(totals.compras)} muted />
      </div>
    ),
    "ga4:weekdays": (
      <Table title="Día que más convierte" headers={["Día", "Compras", "Ingresos"]}
        rows={block.weekdays.map((row) => [String(row.dia), fmtInt(row.compras), fmtMoney(row.ingresos)])}
        coverage={block.coverage} />
    ),
    "ga4:sources": (
      <Table title="Fuentes de tráfico" headers={["Fuente / medio", "Sesiones", "Compras", "Ingresos"]}
        rows={block.sources.map((row) => [String(row.fuente), fmtInt(row.sesiones), fmtInt(row.compras), fmtMoney(row.ingresos)])}
        coverage={block.coverage} />
    ),
  };

  return (
    <>
      <CoverageNote coverage={block.coverage} />
      <SectionShell id="ga4" variant="ga4" title="Sitio web" coverage={block.coverage}
        layout={layout} blocks={blocks} />
    </>
  );
}

function PaidSection({
  block,
  title,
  variant,
  layout,
}: {
  block: ReportPaidBlock;
  title: string;
  variant: "gads" | "meta";
  layout: LayoutControls["layout"];
}) {
  const totals = block.totals;
  const reliable = block.reliability.roasReliable;
  if (block.coverage.state === "SIN_DATOS") {
    return (
      <section className={`rep-section is-${variant}`}>
        <aside className="rep-section-rail"><span>Rendimiento</span><strong>{title}</strong></aside>
        <div className="rep-section-body">
          <EmptyPanel title={title}
            reason={`No hay cuenta de ${title} mapeada con inversión en la ventana. Mapear la cuenta en bridge_cliente_accounts habilita esta sección completa.`} />
        </div>
      </section>
    );
  }

  const campaigns = block.campaigns;
  const byType = (predicate: (type: string) => boolean) =>
    campaigns.filter((row) => predicate(String(row.tipo ?? "")));
  const searchCampaigns = variant === "gads" ? byType((type) => type.toUpperCase().includes("SEARCH")) : [];
  const pmaxCampaigns = variant === "gads" ? byType((type) => type.toUpperCase().includes("PERFORMANCE_MAX")) : [];
  const otherCampaigns = variant === "gads"
    ? byType((type) => !type.toUpperCase().includes("SEARCH") && !type.toUpperCase().includes("PERFORMANCE_MAX"))
    : campaigns;

  const campaignRows = (rows: ReportRow[]) => rows.map((row) => [
    String(row.campana), fmtMoney(row.inversion), fmtInt(row.impresiones), fmtInt(row.clics),
    fmtPct(row.ctr), fmtMoney(row.cpc), fmtInt(row.conversiones),
    block.reliability.cpaReliable ? fmtMoney(row.cpa) : "No confiable",
  ]);
  const campaignHeaders = ["Campaña", "Inversión", "Impresiones", "Clics", "CTR", "CPC", "Conversiones", "Costo/conv."];

  const tiles: Record<string, ReactNode> = {
    [`${variant}:inversion`]: <Tile label="Inversión" value={fmtMoney(totals.inversion)} />,
    [`${variant}:impresiones`]: <Tile label="Impresiones" value={fmtInt(totals.impresiones)} />,
    [`${variant}:clics`]: <Tile label="Clics" value={fmtInt(totals.clics)} />,
    [`${variant}:ctr`]: <Tile label="CTR" value={fmtPct(totals.ctr)} />,
    [`${variant}:cpc`]: <Tile label="CPC medio" value={fmtMoney(totals.cpc)} />,
    [`${variant}:cpm`]: <Tile label="CPM" value={fmtMoney(totals.cpm)} />,
    [`${variant}:conversiones`]: <Tile label="Conversiones" value={fmtInt(totals.conversiones)} />,
    [`${variant}:campanas`]: <Tile label="Campañas activas" value={fmtInt(totals.campanas)} />,
    [`${variant}:rentabilidad`]: reliable ? (
      <>
        <Tile label="Costo por conversión" value={fmtMoney(totals.cpa)} />
        <Tile label="Valor de conversión" value={fmtMoney(totals.valorConversion)} />
        <Tile label="ROAS" value={fmtRatio(totals.roas)} />
      </>
    ) : (
      <div className="rep-tile is-blocked" title={block.reliability.reasons.join(" ")}>
        <span>ROAS y costo por conversión</span>
        <strong>No confiable</strong>
        <i>Medición inválida</i>
      </div>
    ),
  };
  if (variant === "gads") {
    tiles["gads:cuotaImpresiones"] =
      <Tile label="Cuota de impresiones" value={fmtPct(totals.cuotaImpresiones, 1)} />;
  }

  const blocks: Record<string, ReactNode> = {
    [`${variant}:overview`]: (
      <TileGroup
        groupId={`${variant}:overview`}
        layout={layout}
        tiles={tiles}
        aside={<Chart chart={toChart("Comportamiento por día", block.daily, "periodo", [
          { key: "inversion", label: "Inversión", format: "money" },
          { key: "clics", label: "Clics", format: "integer" },
        ], block.coverage)} />}
      />
    ),
  };

  if (!reliable) {
    blocks[`${variant}:alert`] = (
      <div className="rep-alert">
        <AlertTriangle size={16} aria-hidden="true" />
        <div>
          <strong>Las métricas por conversión de esta cuenta no son utilizables como evidencia.</strong>
          <ul>{block.reliability.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
          <p>Se ocultan a propósito en vez de mostrar un número que llevaría a decidir mal. El hallazgo accionable es auditar las acciones de conversión en la plataforma.</p>
        </div>
      </div>
    );
  }

  if (variant === "gads") {
    if (searchCampaigns.length > 0) {
      blocks["gads:search"] = <Table title="Detalle por campaña Search" headers={campaignHeaders}
        rows={campaignRows(searchCampaigns)} coverage={block.coverage} />;
    }
    if (pmaxCampaigns.length > 0) {
      blocks["gads:pmax"] = <Table title="Detalle por campaña PMax" headers={campaignHeaders}
        rows={campaignRows(pmaxCampaigns)} coverage={block.coverage} />;
    }
    if (otherCampaigns.length > 0) {
      blocks["gads:other"] = <Table title="Campañas sin tipo clasificado" headers={campaignHeaders}
        rows={campaignRows(otherCampaigns)} coverage={block.coverage} emptyCopy="Sin campañas adicionales." />;
    }
    blocks["gads:keywords"] = (
      <Table title="Palabras clave" headers={["Palabra clave", "Impresiones", "Clics", "CTR", "Conversiones"]}
        rows={(block.keywords?.rows ?? []).map((row) => [String(row.keyword), fmtInt(row.impresiones), fmtInt(row.clics), fmtPct(row.ctr), fmtInt(row.conversiones)])}
        coverage={block.keywords?.coverage}
        emptyCopy="La ingesta de keywords aún no cubre este cliente." />
    );
    blocks["gads:terms"] = (
      <Table title="Términos de búsqueda" headers={["Término", "Impresiones", "Clics", "CTR", "Conversiones"]}
        rows={(block.searchTerms?.rows ?? []).map((row) => [String(row.termino), fmtInt(row.impresiones), fmtInt(row.clics), fmtPct(row.ctr), fmtInt(row.conversiones)])}
        coverage={block.searchTerms?.coverage}
        emptyCopy="La ingesta de términos de búsqueda aún no cubre este cliente." />
    );
  } else {
    blocks["meta:campaigns"] = <Table title="Detalle por campaña" headers={campaignHeaders}
      rows={campaignRows(campaigns)} coverage={block.coverage} />;
    blocks["meta:ads"] = (
      <article className="rep-panel">
        <div className="rep-panel-head">
          <h3>Muestra de anuncios</h3>
          {block.ads && <CoverageBadge coverage={block.ads.coverage} />}
        </div>
        {block.ads?.rows.length ? (
          <div className="rep-table-scroll">
            <table>
              <thead><tr><th>Anuncio</th><th>Campaña</th><th>Formato</th><th>Impresiones</th><th>Clics</th><th>CTR</th></tr></thead>
              <tbody>
                {block.ads.rows.map((row, index) => (
                  <tr key={index}>
                    <td>{String(row.ad_name)}</td><td>{String(row.campaign_name)}</td><td>{String(row.format)}</td>
                    <td>{fmtInt(row.impresiones)}</td><td>{fmtInt(row.clics)}</td><td>{fmtPct(row.ctr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="rep-empty">La ingesta a nivel de anuncio aún no cubre este cliente.</p>
        )}
        {block.ads?.unavailable.length ? (
          <ul className="rep-missing-list">
            {block.ads.unavailable.map((item) => <li key={item.metric}><strong>{item.metric}:</strong> {item.reason}</li>)}
          </ul>
        ) : null}
      </article>
    );
  }

  return (
    <>
      <CoverageNote coverage={block.coverage} />
      <SectionShell id={variant} variant={variant} title={title} coverage={block.coverage}
        layout={layout} blocks={blocks} />
    </>
  );
}

/** Gertrudis vive dentro del reporte: lee lo mismo que el lector ve, y puede
 *  devolver un gráfico o un cambio de identidad visual además del texto. */
function AnalystDock({
  clientSlug,
  days,
  onTheme,
  onSaveChart,
}: {
  clientSlug: string;
  days: number;
  onTheme: (theme: ReportTheme) => void;
  onSaveChart: (chart: ReportChart) => boolean;
}) {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insight, setInsight] = useState<string | null>(null);
  const [chart, setChart] = useState<ReportChart | null>(null);
  const [notices, setNotices] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/wallet/")
      .then((r) => r.json())
      .then((d) => setBalance(d.balance))
      .catch((e) => console.error("Error fetching balance:", e));
  }, []);

  const ask = useCallback(async (text?: string) => {
    if (balance !== null && balance < 5) {
      setError("Saldo insuficiente. Necesitas 5 tokens para consultar a Gertrudis.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/wallet/consume", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feature_id: "gertrudis_analyst", cost: 5, name: "Gertrudis - Reporte Custom" })
      });
      if (!res.ok) {
        throw new Error("No pudimos descontar los tokens. Verifica tu saldo.");
      }
      const data = await res.json();
      setBalance(data.balance);

      const response = await askReportAnalyst(clientSlug, days, text ?? question);
      setInsight(response.insight);
      setChart(response.chart);
      setSaved(false);
      setNotices(response.notices);
      if (response.theme) onTheme(response.theme);
      setQuestion("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }, [clientSlug, days, question, onTheme, balance]);

  const suggestions = [
    "Dame la lectura ejecutiva del período",
    "Gráfico de tendencia de inversión y clics de los últimos 90 días",
    "¿Qué campaña rinde mejor y cuál cortarías?",
    "Compras por día de la semana en un gráfico",
  ];

  return (
    <section className="rep-analyst">
      <header>
        <Sparkles size={18} aria-hidden="true" />
        <div>
          <strong>Gertrudis · Analista de cuenta</strong>
          <small>
            Lee este mismo reporte. Puede graficar y ajustar los colores de marca. (Costo: 5 tokens, Saldo: {balance !== null ? balance : '...'})
            <button 
              type="button" 
              onClick={() => {
                fetch("/api/wallet/buy", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ tokens: 10, price: "Demo" })
                }).then(() => fetch("/api/wallet/"))
                  .then((r) => r.json())
                  .then((d) => setBalance(d.balance));
              }}
              style={{ marginLeft: "8px", padding: "2px 6px", fontSize: "10px", borderRadius: "4px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", cursor: "pointer", color: "inherit" }}
              title="Sólo para la demo: recargar 10 tokens"
            >
              Recargar
            </button>
          </small>
        </div>
      </header>

      <div className="rep-analyst-input">
        <input
          ref={inputRef}
          value={question}
          placeholder={balance !== null && balance < 5 ? "Necesitas 5 tokens para usar a Gertrudis" : "Pregúntale algo sobre este período…"}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter" && !busy && question.trim()) void ask(); }}
          disabled={busy || (balance !== null && balance < 5)}
        />
        <button type="button" onClick={() => void ask()} disabled={busy || !question.trim() || (balance !== null && balance < 5)}>
          {busy ? <Loader2 size={15} className="rep-spin" /> : <Send size={15} />}
          {busy ? "Analizando" : "Preguntar"}
        </button>
      </div>

      <div className="rep-suggestions">
        {suggestions.map((item) => (
          <button key={item} type="button" disabled={busy || (balance !== null && balance < 5)} onClick={() => { setQuestion(item); void ask(item); }}>
            {item}
          </button>
        ))}
      </div>

      {error && <p className="rep-note is-sin_datos"><AlertTriangle size={13} aria-hidden="true" /><span>{error}</span></p>}
      {notices.map((notice) => (
        <p key={notice} className="rep-note is-parcial"><AlertTriangle size={13} aria-hidden="true" /><span>{notice}</span></p>
      ))}
      {insight && <Markdown source={insight} className="rep-insight" />}
      {chart && (
        <Chart
          chart={chart}
          actions={
            <button
              type="button"
              className="rep-icon-button is-labelled"
              title="Guardar en mis gráficos"
              disabled={saved}
              onClick={() => setSaved(onSaveChart(chart) || true)}
            >
              {saved ? <Check size={13} aria-hidden="true" /> : <BookmarkPlus size={13} aria-hidden="true" />}
              {saved ? "Guardado" : "Guardar"}
            </button>
          }
        />
      )}
    </section>
  );
}

export function ClientReportPage() {
  const { data, selectedClient, setSelectedClient } = useDashboard();
  const clients = data?.clients ?? [];
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState<ReportOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<ReportTheme | null>(null);
  const savedCharts = useReportLayout();

  const client = useMemo(
    () => clients.find((item) => item.clientSlug === selectedClient) ?? clients[0],
    [clients, selectedClient],
  );

  /** Guarda la spec del gráfico, no sus datos: así se redibuja para cualquier cliente. */
  const saveChart = useCallback((chart: ReportChart) => savedCharts.addChart(chart.title, {
    metrics: chart.series.map((serie) => serie.id),
    dimension: chart.dimension.id,
    days: chart.days,
    platform: chart.platform,
    chartType: chart.chartType,
  }), [savedCharts]);

  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchReportOverview(client.clientSlug, days)
      .then((payload) => {
        if (cancelled) return;
        setOverview(payload);
        setTheme(payload.theme);
      })
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [client?.clientSlug, days]);

  if (!client) {
    return (
      <main className="rep-loading">
        <AlertTriangle aria-hidden="true" />
        <h1>No hay clientes disponibles</h1>
        <Link to="/">Volver al dashboard</Link>
      </main>
    );
  }

  const style = theme
    ? { "--rep-primary": theme.primary, "--rep-accent": theme.accent, "--rep-surface": theme.surface } as React.CSSProperties
    : undefined;
  const consolidated = overview?.consolidated;

  return (
    <div className="rep-report" style={style}>
      <header className="rep-header">
        <div className="rep-brand">
          <Link to="/" title="Volver al dashboard operativo"><ArrowLeft size={16} /></Link>
          <span className="rep-logo"><WolfLogo height={38} /></span>
          <i />
          <div>
            <strong>{client.displayName}</strong>
            <small>Reporte de inversión · data en tiempo real</small>
          </div>
        </div>
        <div className="rep-controls">
          <label>
            <span>Cliente</span>
            <select value={client.clientSlug} onChange={(event) => setSelectedClient(event.target.value)}>
              {clients.map((item) => <option key={item.clientSlug} value={item.clientSlug}>{item.displayName}</option>)}
            </select>
            <ChevronDown size={14} aria-hidden="true" />
          </label>
          <div className="rep-periods" role="group" aria-label="Período">
            {PERIODS.map((value) => (
              <button key={value} type="button" className={days === value ? "is-active" : ""} onClick={() => setDays(value)}>
                {value} días
              </button>
            ))}
          </div>
          <button
            type="button"
            className={savedCharts.editing ? "rep-edit-toggle is-active" : "rep-edit-toggle"}
            onClick={() => savedCharts.setEditing(!savedCharts.editing)}
            title="Reordenar y ocultar bloques"
          >
            {savedCharts.editing ? <Check size={14} aria-hidden="true" /> : <Pencil size={14} aria-hidden="true" />}
            {savedCharts.editing ? "Listo" : "Editar layout"}
          </button>
        </div>
      </header>

      {loading && <div className="rep-loading-bar"><Loader2 size={15} className="rep-spin" /> Consultando el datalake…</div>}
      {error && (
        <div className="rep-loading">
          <AlertTriangle aria-hidden="true" />
          <h1>No fue posible construir el reporte</h1>
          <p>{error}</p>
        </div>
      )}

      {overview && !error && (
        <main className="rep-main">
          <section className="rep-summary">
            <div>
              <span>Inversión total</span>
              <strong>{fmtMoney(consolidated?.inversionTotal)}</strong>
              <small>Google Ads + Meta en la ventana completa</small>
            </div>
            <div>
              <span>Ingresos del sitio</span>
              <strong>{fmtMoney(consolidated?.ingresosTotales)}</strong>
              <small>Todos los canales, medido en GA4</small>
            </div>
            <div>
              <span>Retorno sobre inversión</span>
              <strong>{consolidated?.roasGlobal == null ? "—" : fmtRatio(consolidated.roasGlobal)}</strong>
              <small>{consolidated?.roasWindow ? `Ventana comparable: ${consolidated.roasWindow}` : "Sin ventana comparable"}</small>
            </div>
            <div className="rep-summary-period">
              <span>Período</span>
              <strong>{overview.window.from} a {overview.window.to}</strong>
              <small>{days} días solicitados</small>
            </div>
          </section>

          {consolidated?.note && (
            <p className="rep-note is-parcial"><AlertTriangle size={13} aria-hidden="true" /><span>{consolidated.note}</span></p>
          )}

          <AnalystDock
            clientSlug={client.clientSlug}
            days={days}
            onTheme={setTheme}
            onSaveChart={saveChart}
          />

          <ReportChartLibrary
            clientSlug={client.clientSlug}
            charts={savedCharts.layout.charts}
            onAdd={savedCharts.addChart}
            onRemove={savedCharts.removeChart}
            onReorder={savedCharts.moveChart}
          />

          {savedCharts.editing && (
            <HiddenTray
              hidden={savedCharts.layout.hidden}
              labels={BLOCK_LABELS}
              onShow={savedCharts.show}
              onReset={savedCharts.resetLayout}
            />
          )}

          {/* Las secciones se dibujan en el orden guardado por el usuario; cada
              una decide internamente el orden de sus bloques y cards. */}
          {savedCharts.layout.sections
            .filter((sectionId) => !savedCharts.isHidden(sectionId))
            .map((sectionId) => {
              if (sectionId === "ga4") {
                return <Ga4Section key={sectionId} block={overview.ga4} layout={savedCharts} />;
              }
              const isGads = sectionId === "gads";
              return (
                <PaidSection
                  key={sectionId}
                  block={isGads ? overview.gads : overview.meta}
                  title={isGads ? "Google Ads" : "Meta Ads"}
                  variant={isGads ? "gads" : "meta"}
                  layout={savedCharts}
                />
              );
            })}
        </main>
      )}

      <footer className="rep-footer">
        <span><BarChart3 size={13} aria-hidden="true" /> Cada bloque declara si su fuente es real, parcial o no está conectada</span>
      </footer>
    </div>
  );
}
